import PolicyLayout, { H2, P, UL } from "./PolicyLayout";
import { useSettings } from "../../contexts/SettingsContext";

export default function ShippingPolicy() {
  // iter101 — the "Payment mode" bullet below reflects the admin COD
  // toggle so we never advertise COD when it's currently disabled.
  const { codEnabled } = useSettings();
  return (
    <PolicyLayout
      title="Shipping Policy"
      testid="page-shipping-policy"
      canonical="/shipping-policy"
      seoTitle="Shipping Policy | Sri Durga Foods"
      seoDescription="Delivery timelines, courier partners, shipping charges and serviceability at Sri Durga Foods — pan-India delivery via Shiprocket."
    >
      <P>
        Sri Durga Foods ships authentic Andhra pickles, pastes, spices and
        traditional foods all across India through our logistics partner{" "}
        <strong>Shiprocket</strong>. This Shipping Policy explains how
        orders are processed, how shipping charges are calculated, and what
        to expect after placing an order.
      </P>

      <H2>1. Order processing time</H2>
      <UL>
        <li>Orders placed before 2:00 PM IST on a working day are processed the same day; orders after 2:00 PM IST are processed the following working day.</li>
        <li>Standard processing time is 24–48 hours after order confirmation.</li>
        <li>Orders are not processed or dispatched on Sundays and public holidays.</li>
        <li>You will receive a WhatsApp / email notification once your order is dispatched, along with the courier tracking number.</li>
      </UL>

      <H2>2. Shipping partner</H2>
      <P>
        We ship through <strong>Shiprocket</strong>, which assigns the most
        reliable and economical courier partner (Delhivery, Blue Dart, Ekart,
        DTDC, Xpressbees, Amazon Shipping and others) based on your pincode,
        package weight and serviceability. The assigned courier may differ
        between orders.
      </P>

      <H2>3. Estimated delivery timelines</H2>
      <UL>
        <li><strong>Metro cities</strong> (Hyderabad, Chennai, Bengaluru, Mumbai, Delhi, Kolkata): 3–5 business days.</li>
        <li><strong>Tier-2 cities</strong>: 4–6 business days.</li>
        <li><strong>Remote / Tier-3 locations</strong>: 5–9 business days.</li>
        <li>Timelines are estimates only and may vary due to courier operations, weather, festivals or public holidays.</li>
      </UL>

      <H2>4. Shipping charges</H2>
      <P>
        Shipping charges on Sri Durga Foods are <strong>calculated
        dynamically through the Shiprocket API</strong> at the time of
        checkout. The exact amount you pay depends on the live courier
        quote for your order, determined by:
      </P>
      <UL>
        <li><strong>Destination pincode</strong> — distance from our warehouse in Nellore (524003), Andhra Pradesh.</li>
        <li><strong>Total shipment weight</strong> — calculated from the size variants and quantities in your cart.</li>
        <li><strong>Courier availability and serviceability</strong> at the destination.</li>
        {codEnabled && (
          <li><strong>Payment mode</strong> — Cash-on-Delivery (COD) orders may incur a small handling fee where applicable.</li>
        )}
      </UL>
      <P>
        You can enter your pincode on the Cart page to see an estimated
        delivery charge before checkout. The final shipping charge is
        confirmed against your selected delivery address at the Checkout
        step.
      </P>

      <H2>5. Order tracking</H2>
      <P>
        Once your order is dispatched, you will receive a tracking number
        via WhatsApp / email. You can use this number on the courier
        partner&apos;s website (or on shiprocket.in/tracking) to monitor your
        shipment in real time.
      </P>

      <H2>6. Delays and exceptions</H2>
      <UL>
        <li>Deliveries may be delayed during monsoons, strikes, natural calamities or major festivals.</li>
        <li>Multiple failed delivery attempts may result in the package being returned to origin (RTO). In such cases, please contact our support team for re-shipping options; additional charges may apply.</li>
        <li>For perishable / temperature-sensitive items, please ensure someone is available at the delivery address.</li>
      </UL>

      <H2>7. Non-serviceable locations</H2>
      <P>
        Certain remote pincodes may be temporarily non-serviceable by our
        courier partners. If your pincode is non-serviceable, the website
        will indicate this on the Cart page when you enter your pincode.
        Please contact us via WhatsApp — in many cases we can arrange an
        alternative shipment.
      </P>

      <H2>8. International shipping</H2>
      <P>
        At present we ship within India only. International shipping is not
        offered through the Website. If you are an NRI or overseas
        customer, please reach out via WhatsApp for special arrangements.
      </P>

      <H2>9. Customer responsibilities</H2>
      <UL>
        <li>Provide an accurate delivery address, contact number and 6-digit pincode at checkout.</li>
        <li>Ensure someone is available to receive the parcel during the courier&apos;s working hours.</li>
        <li>Inspect the parcel on arrival and report damages/missing items within 24 hours of delivery.</li>
      </UL>
    </PolicyLayout>
  );
}
